import { Phone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CalculatorButton } from "./CalculatorButton";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/landingLinks";

/**
 * The top of a service page.
 *
 * Two columns on a large screen: the promise on the left, a short credential
 * stack on the right so the qualifications are visible without a paragraph
 * about them. One column on a phone, with the headline, one button and the
 * phone number inside the first screen.
 *
 * The teal wash and the ruled-paper texture are the same two layers the
 * calculator's own hero uses, which is what makes these pages read as part of
 * the same product rather than a separate microsite.
 */
export const LandingHero = ({
  eyebrow,
  heading,
  highlight,
  lead,
  ctaLabel,
  service,
  note,
  slug,
  credentials,
  credentialsTitle = "Who does the work",
  credentialsNote,
}) => (
  <section data-testid={`${slug}-hero`} className="relative overflow-hidden">
    <div className="tt-hero-wash tt-hero-fade absolute inset-0" aria-hidden="true" />
    <div className="tt-ruled tt-hero-fade absolute inset-0 opacity-50" aria-hidden="true" />

    <div className="relative mx-auto max-w-6xl px-4 pb-12 pt-10 sm:px-6 sm:pb-16 sm:pt-16 lg:px-8">
      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          <span className="tt-stamp">{eyebrow}</span>

          {/* The accent phrase gets its own line, the way the calculator's own
              hero sets "before you pick up the phone." A phrase like "business
              credibility" cannot fit beside the heading on a phone, and keeping
              it inline left the keyline running into the edge of the screen. */}
          <h1
            data-testid={`${slug}-h1`}
            className="mt-6 text-[34px] font-extrabold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-[46px] lg:text-[54px]"
          >
            {heading}
            {highlight ? (
              <>
                <br />
                <span className="relative inline-block max-w-full text-primary">
                  {highlight}
                  <span
                    aria-hidden="true"
                    className="absolute -bottom-1.5 left-0 h-[3px] w-full rounded-full bg-[color:var(--tt-teal-line)]"
                  />
                </span>
              </>
            ) : null}
          </h1>

          <p className="mt-7 max-w-[54ch] text-base leading-relaxed text-muted-foreground sm:text-[17px]">
            {lead}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <CalculatorButton service={service} testId={`${slug}-hero-cta`}>
              {ctaLabel}
            </CalculatorButton>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid={`${slug}-hero-call`}
              className="h-12 min-h-[44px] bg-white px-5 text-[15px] font-semibold"
            >
              <a href={PHONE_HREF}>
                <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
                {PHONE_DISPLAY}
              </a>
            </Button>
          </div>

          <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">{note}</p>
        </div>

        <div className="lg:col-span-5">
          <div
            data-testid={`${slug}-credentials-card`}
            className="tt-card p-6 sm:p-7"
          >
            <h2 className="tt-eyebrow">{credentialsTitle}</h2>
            <ul className="mt-5 space-y-4">
              {credentials.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex items-start gap-3.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color:var(--tt-accent)]/10 text-[color:var(--tt-accent)]">
                    <Icon className="h-4.5 w-4.5" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground">{title}</span>
                    <span className="mt-0.5 block text-[13px] leading-relaxed text-muted-foreground">
                      {text}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            {credentialsNote ? (
              <p className="mt-6 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
                {credentialsNote}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  </section>
);
